/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-11 22:49:43
 **/

class JsonUtilStatic {
	/**
	 * Checks if the given data can be parsed into a JSON object.
	 *
	 * @param {unknown} data - The data to be evaluated.
	 * @return {boolean} Returns true if the data can be parsed into a JSON object, otherwise false.
	 */
	public is_json_object(data: unknown): boolean {
		try {
			return typeof this.to_object(data) !== "string";
		} catch {
			return false;
		}
	}

	/**
	 * Converts the given data to a string representation.
	 *
	 * @param {any} data - The data to be converted to string.
	 * @return {string|null} - The string representation of the data, or null if no data is provided.
	 */
	public to_string(data: unknown): string | null {
		if (data) {
			if (typeof data === "object") {
				return JSON.stringify(data);
			}
			return data as string;
		}
		return null;
	}

	/**
	 * Converts a given data input into an object of type T. If the conversion fails, returns a default value.
	 *
	 * @param {any} data The input data to be converted to an object.
	 * @param {T | null} [defaultValue=null] The default value to return if the conversion fails.
	 * @return {T | null} The converted object of type T, or the default value if conversion fails.
	 */
	public to_object<T = unknown>(data: unknown, defaultValue: T | null = null): T | null {
		try {
			if (typeof data === "string" && (data === "true" || data === "false")) {
				return JSON.parse(data) as T;
			}
			if (typeof data === "string" && (data.trim().startsWith("{") || data.trim().startsWith("["))) {
				return JSON.parse(data) as T;
			}
			return data as T;
		} catch {
			return defaultValue;
		}
	}
}

const JsonUtils = Object.freeze(new JsonUtilStatic());
export default JsonUtils;
